/**
 * Pip geometry — the deterministic vector layout for number cards (A–10) and
 * the four suit-glyph shapes. Pure geometry, no React: this is the structural
 * data `CardFace` draws. Geometric SVG used as structural UI is allowed (it must
 * be crisp at any size and identical in light & dark — generation can't do that);
 * the *depictive* court figures come from the asset pipeline instead.
 *
 * Spacing note: the columns and rows below were TIGHTENED from the original
 * Solitaire layout. The previous grid pushed pips to the card edges (cols at
 * 30/70, rows from 18 to 82) which read as loose and floaty at ~120px. These
 * pull the grid inward (cols at 33/67, the vertical band compressed) for the
 * denser, calmer classic look the owner asked for.
 */
import type { Rank, Suit } from './model';

// ---------------------------------------------------------------------------
// Suit glyphs — single <path> shapes in a 0..100 viewBox, positioned by the
// layout grid. Suits read by SHAPE first (colour-blind safe).
// ---------------------------------------------------------------------------
export const SUIT_PATH: Record<Suit, string> = {
  // Heart — two lobes into a point.
  hearts:
    'M50 86 C24 64 8 50 8 32 C8 18 19 9 31 9 C40 9 47 14 50 22 C53 14 60 9 69 9 C81 9 92 18 92 32 C92 50 76 64 50 86 Z',
  // Diamond — a rhombus with slightly convex sides.
  diamonds:
    'M50 6 C58 26 70 40 86 50 C70 60 58 74 50 94 C42 74 30 60 14 50 C30 40 42 26 50 6 Z',
  // Spade — inverted heart with a stem.
  spades:
    'M50 8 C66 30 92 42 92 62 C92 74 83 82 72 82 C64 82 58 78 54 72 C56 80 60 88 68 93 L32 93 C40 88 44 80 46 72 C42 78 36 82 28 82 C17 82 8 74 8 62 C8 42 34 30 50 8 Z',
  // Club — three lobes on a stem.
  clubs:
    'M50 6 C61 6 70 15 70 26 C70 31 68 35 65 39 C71 35 77 33 83 33 C93 33 99 41 99 50 C99 60 91 67 81 67 C73 67 66 63 61 56 C63 66 68 78 76 93 L24 93 C32 78 37 66 39 56 C34 63 27 67 19 67 C9 67 1 60 1 50 C1 41 7 33 17 33 C23 33 29 35 35 39 C32 35 30 31 30 26 C30 15 39 6 50 6 Z',
};

export const RANK_LABEL: Record<Rank, string> = {
  1: 'A',
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  6: '6',
  7: '7',
  8: '8',
  9: '9',
  10: '10',
  11: 'J',
  12: 'Q',
  13: 'K',
};

export function isRed(suit: Suit): boolean {
  return suit === 'hearts' || suit === 'diamonds';
}

export function isCourt(rank: Rank): boolean {
  return rank === 11 || rank === 12 || rank === 13;
}

// ---------------------------------------------------------------------------
// Pip layouts — canonical (x%, y%) positions on a 0..100 inner field for 2..10.
// A pip in the bottom half (y > 50) is rendered upside-down, like a real card.
//
// Columns pulled in to 33 / 67 (from 30 / 70) and the vertical band tightened
// (top row 20, bottom row 80, from 18 / 82) so the grid reads dense, not floaty.
// ---------------------------------------------------------------------------
export type Pt = readonly [number, number];

const COL_L = 33;
const COL_C = 50;
const COL_R = 67;
const ROW_T = 20;
const ROW_B = 80;

export const PIP_LAYOUT: Partial<Record<Rank, readonly Pt[]>> = {
  2: [
    [COL_C, ROW_T],
    [COL_C, ROW_B],
  ],
  3: [
    [COL_C, ROW_T],
    [COL_C, 50],
    [COL_C, ROW_B],
  ],
  4: [
    [COL_L, ROW_T],
    [COL_R, ROW_T],
    [COL_L, ROW_B],
    [COL_R, ROW_B],
  ],
  5: [
    [COL_L, ROW_T],
    [COL_R, ROW_T],
    [COL_C, 50],
    [COL_L, ROW_B],
    [COL_R, ROW_B],
  ],
  6: [
    [COL_L, ROW_T],
    [COL_R, ROW_T],
    [COL_L, 50],
    [COL_R, 50],
    [COL_L, ROW_B],
    [COL_R, ROW_B],
  ],
  7: [
    [COL_L, ROW_T],
    [COL_R, ROW_T],
    [COL_C, 35],
    [COL_L, 50],
    [COL_R, 50],
    [COL_L, ROW_B],
    [COL_R, ROW_B],
  ],
  8: [
    [COL_L, ROW_T],
    [COL_R, ROW_T],
    [COL_C, 35],
    [COL_L, 50],
    [COL_R, 50],
    [COL_C, 65],
    [COL_L, ROW_B],
    [COL_R, ROW_B],
  ],
  9: [
    [COL_L, 19],
    [COL_R, 19],
    [COL_L, 40],
    [COL_R, 40],
    [COL_C, 50],
    [COL_L, 60],
    [COL_R, 60],
    [COL_L, 81],
    [COL_R, 81],
  ],
  10: [
    [COL_L, 17],
    [COL_R, 17],
    [COL_C, 28],
    [COL_L, 39],
    [COL_R, 39],
    [COL_L, 61],
    [COL_R, 61],
    [COL_C, 72],
    [COL_L, 83],
    [COL_R, 83],
  ],
};
